import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import WhatsAppButton from "@/components/public/WhatsAppButton";
import { Link } from "wouter";
import { BRAND } from "@/brand";
import { useSeo } from "@/hooks/useSeo";

export default function CookiesPage() {
  useSeo({ title: "Política de cookies | Golf Motion Lab" });
  const lastUpdated = "29 de mayo de 2026";

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <PublicHeader />

      <section className="container py-20 md:py-32 max-w-3xl">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-4">
          Información legal
        </div>
        <h1
          className="font-display font-light text-[var(--forest-deep)] leading-[1.05] mb-6"
          style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}
        >
          Política de cookies
        </h1>
        <p className="text-sm text-[var(--forest-deep)]/60 mb-12">
          Última actualización: {lastUpdated}
        </p>

        <div className="prose prose-neutral max-w-none text-[var(--forest-deep)]/85 leading-relaxed">
          <p>
            La presente Política de Cookies explica qué son las cookies, qué tipos utiliza el
            sitio web <strong>{BRAND.domain}</strong>, titularidad de <strong>{BRAND.name}</strong>,
            y cómo puedes gestionarlas, en cumplimiento del artículo 22.2 de la Ley 34/2002, de
            Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), del
            Reglamento (UE) 2016/679 (RGPD) y de la Guía sobre el uso de cookies de la Agencia
            Española de Protección de Datos (AEPD).
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            1. ¿Qué es una cookie?
          </h2>
          <p>
            Una cookie es un pequeño archivo de texto que un sitio web almacena en el navegador o
            el dispositivo de la persona usuaria cuando visita una página. Las cookies permiten,
            entre otras cosas, recordar preferencias, mantener una sesión iniciada, garantizar el
            correcto funcionamiento del sitio o, en algunos casos, recoger información estadística
            o publicitaria.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            2. Tipos de cookies utilizadas en este sitio
          </h2>
          <p>
            {BRAND.name} utiliza exclusivamente <strong>cookies técnicas o estrictamente
            necesarias</strong> para el funcionamiento del sitio web. No se utilizan cookies
            analíticas, publicitarias, de personalización ni de seguimiento de comportamiento.
            Conforme al artículo 22.2 de la LSSI-CE, las cookies técnicas no requieren
            consentimiento previo, ya que resultan imprescindibles para la prestación del
            servicio solicitado.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            3. Tabla de cookies
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[var(--forest)]/20">
                  <th className="text-left py-2 pr-3 font-semibold text-[var(--forest-deep)]">
                    Nombre
                  </th>
                  <th className="text-left py-2 pr-3 font-semibold text-[var(--forest-deep)]">
                    Finalidad
                  </th>
                  <th className="text-left py-2 pr-3 font-semibold text-[var(--forest-deep)]">
                    Tipo
                  </th>
                  <th className="text-left py-2 pr-3 font-semibold text-[var(--forest-deep)]">
                    Duración
                  </th>
                  <th className="text-left py-2 font-semibold text-[var(--forest-deep)]">
                    Proveedor
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--forest)]/10">
                <tr>
                  <td className="py-3 pr-3 font-mono text-xs">session</td>
                  <td className="py-3 pr-3">
                    Mantener la sesión iniciada del usuario en la zona privada y validar la
                    autenticación.
                  </td>
                  <td className="py-3 pr-3">Técnica / propia</td>
                  <td className="py-3 pr-3">Sesión</td>
                  <td className="py-3">{BRAND.domain}</td>
                </tr>
                <tr>
                  <td className="py-3 pr-3 font-mono text-xs">sidebar_state</td>
                  <td className="py-3 pr-3">
                    Recordar si el menú lateral del panel interno está abierto o plegado.
                  </td>
                  <td className="py-3 pr-3">Técnica / propia</td>
                  <td className="py-3 pr-3">7 días</td>
                  <td className="py-3">{BRAND.domain}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[var(--forest-deep)]/60 mt-3">
            Esta tabla puede actualizarse cuando se incorporen nuevas funcionalidades al sitio
            web. Cualquier nueva cookie no técnica se incluirá previo aviso y, en su caso, previo
            consentimiento del usuario.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            4. Aviso de cookies y registro de la elección
          </h2>
          <p>
            Al acceder por primera vez al sitio, mostramos un aviso en la parte inferior con
            las opciones <strong>Aceptar</strong> o <strong>Rechazar</strong>. Tu elección se
            guarda en el almacenamiento local del navegador (“localStorage”), no en una cookie,
            con la clave <code>gml-cookie-consent</code> y el valor <code>accepted</code> o
            <code>rejected</code>. Este registro queda en tu propio dispositivo, no se envía a
            {" "}{BRAND.name} y solo sirve para no volver a mostrarte el aviso. Puedes borrarlo
            en cualquier momento desde la configuración de tu navegador (“Borrar datos del
            sitio”) y el banner volverá a aparecer en tu siguiente visita.
          </p>
          <p>
            Dado que en este sitio solo se utilizan cookies estrictamente necesarias, rechazar
            el aviso no desactiva ninguna cookie adicional: simplemente refleja tu preferencia y
            ratifica que no se cargarán cookies no técnicas en el futuro sin solicitarte
            consentimiento explícito.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            5. Cookies de terceros
          </h2>
          <p>
            En la actualidad <strong>{BRAND.domain}</strong> no incorpora cookies de terceros
            (analíticas, publicitarias, mapas embebidos, vídeos externos u otras). En el caso de
            que en el futuro se incorpore alguna funcionalidad que requiera el uso de cookies de
            terceros, se actualizará esta política y se solicitará, cuando proceda, el
            consentimiento previo del usuario.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            6. Cómo gestionar y desactivar las cookies
          </h2>
          <p>
            Puedes permitir, bloquear o eliminar las cookies instaladas en tu equipo desde la
            configuración del navegador. A continuación te indicamos los enlaces de ayuda de los
            principales navegadores:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <a
                href="https://support.google.com/chrome/answer/95647"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--forest)] underline"
              >
                Google Chrome
              </a>
            </li>
            <li>
              <a
                href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--forest)] underline"
              >
                Mozilla Firefox
              </a>
            </li>
            <li>
              <a
                href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--forest)] underline"
              >
                Apple Safari
              </a>
            </li>
            <li>
              <a
                href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--forest)] underline"
              >
                Microsoft Edge
              </a>
            </li>
            <li>
              <a
                href="https://help.opera.com/en/latest/web-preferences/#cookies"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--forest)] underline"
              >
                Opera
              </a>
            </li>
          </ul>
          <p>
            Ten en cuenta que la desactivación de las cookies técnicas puede impedir el correcto
            funcionamiento de algunas áreas del sitio web, en particular de la zona privada del
            jugador.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            7. Actualizaciones de esta política
          </h2>
          <p>
            {BRAND.name} podrá modificar esta Política de Cookies en función de exigencias
            legislativas, reglamentarias, o con la finalidad de adaptarla a las instrucciones
            dictadas por la AEPD. Cuando se introduzcan cambios significativos en esta Política,
            se notificará a las personas usuarias mediante un aviso visible en el sitio web.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            8. Más información
          </h2>
          <p>
            Para más información sobre cómo tratamos los datos personales, consulta nuestra{" "}
            <Link href="/privacidad" className="text-[var(--forest)] underline">
              Política de Privacidad
            </Link>{" "}
            y nuestro{" "}
            <Link href="/aviso-legal" className="text-[var(--forest)] underline">
              Aviso Legal
            </Link>
            . Si tienes cualquier duda, puedes escribir a{" "}
            <a href={`mailto:${BRAND.email}`} className="text-[var(--forest)] underline">
              {BRAND.email}
            </a>
            .
          </p>

          <p className="text-sm text-[var(--forest-deep)]/60 italic mt-16">
            Este texto se ha redactado siguiendo la Guía sobre el uso de cookies de la AEPD y la
            LSSI-CE. Antes de su publicación definitiva deberá ser revisado por un asesor legal.
          </p>
        </div>
      </section>

      <PublicFooter />
      <WhatsAppButton />
    </div>
  );
}
